import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

export const PAGE_SIZE = 10;

export function SearchBar({ value, onChange, placeholder = "Cari nama, kode, atau lokasi...", testid }) {
  return (
    <div className="relative w-full max-w-sm">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        className="pl-9 bg-card"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}

export function matchesSearch(obj, fields, q) {
  if (!q || !q.trim()) return true;
  const s = q.toLowerCase();
  return fields.some((f) => (obj[f] || "").toString().toLowerCase().includes(s));
}

export function usePagination(count) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  return { page: safePage, setPage, pageCount };
}

export const onPage = (idx, page) => idx >= (page - 1) * PAGE_SIZE && idx < page * PAGE_SIZE;

// Rows outside the current page are hidden on screen but still printed.
export const printRowClass = (idx, page) => (onPage(idx, page) ? "" : "hidden print:table-row");

export function PaginationNav({ page, pageCount, onChange, testid }) {
  if (pageCount <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border print:hidden" data-testid={testid}>
      <p className="text-xs text-muted-foreground" data-testid={`${testid}-info`}>
        Halaman {page} dari {pageCount}
      </p>
      <div className="flex gap-1">
        <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => onChange(page - 1)} data-testid={`${testid}-prev`} title="Halaman sebelumnya">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="icon" disabled={page >= pageCount} onClick={() => onChange(page + 1)} data-testid={`${testid}-next`} title="Halaman berikutnya">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export function DateFilter({ mode, setMode, value, setValue, testid, rooms, room, setRoom }) {
  return (
    <div className="flex flex-wrap items-end gap-2 print:hidden" data-testid={testid}>
      {rooms && (
        <div className="space-y-1">
          <Label className="text-xs">Ruangan</Label>
          <Select value={room} onValueChange={setRoom}>
            <SelectTrigger className="w-[160px]" data-testid={`${testid}-room`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="semua">Semua Ruangan</SelectItem>
              {rooms.map((r) => (
                <SelectItem key={r.id} value={r.nama}>{r.nama}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-1">
        <Label className="text-xs">Filter Periode</Label>
        <Select value={mode} onValueChange={(v) => { setMode(v); setValue(""); }}>
          <SelectTrigger className="w-[140px]" data-testid={`${testid}-mode`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="semua">Semua</SelectItem>
            <SelectItem value="tanggal">Tanggal</SelectItem>
            <SelectItem value="bulan">Bulan</SelectItem>
            <SelectItem value="tahun">Tahun</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {mode === "tanggal" && (
        <Input type="date" value={value} onChange={(e) => setValue(e.target.value)} className="w-[160px]" data-testid={`${testid}-date`} />
      )}
      {mode === "bulan" && (
        <Input type="month" value={value} onChange={(e) => setValue(e.target.value)} className="w-[160px]" data-testid={`${testid}-month`} />
      )}
      {mode === "tahun" && (
        <Input type="number" min="2000" max="2100" placeholder="2026" value={value} onChange={(e) => setValue(e.target.value)} className="w-[110px]" data-testid={`${testid}-year`} />
      )}
    </div>
  );
}

export function ConfirmDialog({ open, onOpenChange, onConfirm, title = "Apakah Anda yakin?", description, confirmLabel = "Ya, Lanjutkan", testid }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel`}>Batal</Button>
          <Button variant="destructive" onClick={onConfirm} data-testid={`${testid}-confirm`}>{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useRooms() {
  const [rooms, setRooms] = useState([]);
  useEffect(() => {
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
  }, []);
  return rooms;
}

export function PrintSignature({ leftTitle = "Teknisi IT", leftName, leftNip, rightTitle = "Kepala Ruangan", rightName, rightNip }) {
  return (
    <div className="hidden print:grid grid-cols-2 gap-8 mt-14 px-10 pb-8 text-center text-sm" data-testid="print-signature">
      <div>
        <p>{leftTitle},</p>
        <div className="h-16" />
        <p className="font-semibold underline underline-offset-4">{leftName || "(____________________)"}</p>
        <p>NIP. {leftNip || "____________________"}</p>
      </div>
      <div>
        <p>{rightTitle},</p>
        <div className="h-16" />
        <p className="font-semibold underline underline-offset-4">{rightName || "(____________________)"}</p>
        <p>NIP. {rightNip || "____________________"}</p>
      </div>
    </div>
  );
}

export function matchDate(dateStr, mode, value) {
  if (mode === "semua" || !value) return true;
  if (!dateStr) return false;
  const d = dateStr.slice(0, 10); // YYYY-MM-DD
  if (mode === "tanggal") return d === value;
  if (mode === "bulan") return d.slice(0, 7) === value;
  if (mode === "tahun") return d.slice(0, 4) === value.toString();
  return true;
}
